<template>
    <q-input
        for="password-field"
        outlined
        bg-color="white"
        lazy-rules
        v-model="password.new"
        :type="showPassword ? 'input' : 'password'"
        :label="$t('New Password')"
        :rules="[validateNewPassword]"
        autocomplete="off"
    >
        <template v-slot:append>
            <q-icon
                v-show="password.new.length > 0"
                @click="showPassword = !showPassword"
                :name="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"
                class="cursor-pointer"
            />
        </template>
        <template v-slot:hint>
            <q-linear-progress
                v-show="password.new.length > 0"
                :value="progressBarValue"
                :color="progressBarColor"
            />
        </template>
    </q-input>
    <q-space class="q-py-xs" />
    <q-input
        for="confirm-field"
        outlined
        lazy-rules
        bg-color="white"
        v-model="password.confirmation"
        :type="showPassword ? 'input' : 'password'"
        :label="$t('Confirm Password')"
        :rules="[isConfirmedPassword]"
        color="accent"
        autocomplete="off"
    />
    <q-slide-transition>
        <div v-show="password.new.length > 0">
            <div class="q-pl-sm text-secondary">
                <div :class="hasSameValue ? 'text-green' : ''">
                    <q-icon
                        class="q-mr-sm"
                        :name="hasSameValue ? 'fas fa-check' : 'fas fa-xmark'"
                    />
                    <span>{{ $t('The passwords must match') }}</span>
                </div>
                <div :class="hasMinimumLength ? 'text-green' : ''">
                    <q-icon
                        class="q-mr-sm"
                        :name="
                            hasMinimumLength ? 'fas fa-check' : 'fas fa-xmark'
                        "
                    />
                    <span>{{
                        $t('The password must be a minimum of 12 characters')
                    }}</span>
                </div>
                <div :class="hasMaximumLength ? 'text-green' : ''">
                    <q-icon
                        class="q-mr-sm"
                        :name="
                            hasMaximumLength ? 'fas fa-check' : 'fas fa-xmark'
                        "
                    />
                    <span>{{
                        $t('The password must be a maximum of 128 characters')
                    }}</span>
                </div>
            </div>
        </div>
    </q-slide-transition>
</template>

<script>
export default {
    name: 'PasswordAndConfirmation',
    emits: ['inputUpdate'],
    props: {
        currentPassword: {
            type: String,
            required: false
        }
    },
    data() {
        return {
            // Set password minimum and maximum length
            PASSWORD_MIN_LENGTH: 12,
            PASSWORD_MAX_LENGTH: 128,
            password: {
                new: '',
                confirmation: '',
                isConfirmed: false
            },
            showPassword: false
        };
    },
    watch: {
        /**
         * For every keypress event on the input, emit an
         * inputUpdate event to update parent component
         */
        password: {
            handler() {
                // If the password is valid, update password object
                if (this.isValidPassword) {
                    this.password.isConfirmed = true;
                } else {
                    this.password.isConfirmed = false;
                }
                this.$emit('inputUpdate', this.password);
            },
            deep: true
        }
    },
    computed: {
        /**
         * Checks if the password has the minimum length
         * @return {Boolean}
         */
        hasMinimumLength() {
            return this.password.new.length >= this.PASSWORD_MIN_LENGTH;
        },
        /**
         * Checks if the password has the maximum length
         * @return {Boolean}
         */
        hasMaximumLength() {
            return this.password.new.length <= this.PASSWORD_MAX_LENGTH;
        },
        /**
         * Checks if the password and confirmation has the same value
         * @return {Boolean}
         */
        hasSameValue() {
            return (
                this.password.confirmation.localeCompare(this.password.new) ===
                0
            );
        },
        /**
         * Checks if the password passes all validation
         * @return {Boolean}
         */
        isValidPassword() {
            return (
                this.hasMinimumLength &&
                this.hasMaximumLength &&
                this.hasSameValue
            );
        },
        /**
         * Returns a float between 0 and 1 representing the percentage of the password
         * @return {Number}
         */
        progressBarValue() {
            return this.password.new.length / this.PASSWORD_MIN_LENGTH;
        },
        /**
         * Returns the color of the progress bar
         * @return {String}
         */
        progressBarColor() {
            return this.progressBarValue < 1 ? 'secondary' : 'positive';
        }
    },
    methods: {
        /**
         * Validate the new password
         * @param {String} val - User input from password field.
         * @return {Boolean} - Valid inputs returns true.
         * @return {String} - Error message.
         */
        validateNewPassword(val) {
            if (val.length === 0) {
                return this.$t('Enter a password');
            } else if (val === this.currentPassword) {
                return this.$t(
                    'You cannot reuse the same password. Please input a new one.'
                );
            }
            return true;
        },
        /**
         * Validate the confirmed password.
         * @param {string} input - User's input from confirmed password field.
         * @returns {(string|void)} - Error message.
         */
        isConfirmedPassword(input) {
            if (input === '') {
                return this.$t('Confirm your password');
            }
            return true;
        }
    }
};
</script>
